import ApplicationController from './application_controller'
import { phoneValidationError } from '../lib/phone_validation'

/**
 * Strict country validation (customer-facing widget only):
 * a `messages` value ({ too_short, too_long, invalid }) opts the field in, see
 * FormBuilder::TelephoneField. The number is then checked against the selected
 * flag on blur, on flag change and on form submit, and an inline error is shown
 * (and cleared as soon as the number becomes valid). Fields without it — the
 * Ops / back-office forms — keep the permissive behaviour.
 */
export default class extends ApplicationController {
  static targets = [ 'input', 'countryCode', 'error' ]
  static values = { code: String, identifier: String, messages: Object }

  connect () {
    this.loadTel()
    if (this.strictValidation) this.observeValidation()
  }

  disconnect () {
    this.inputTarget.removeEventListener('blur', this.onBlur)
    this.inputTarget.removeEventListener('input', this.onInput)
    this.form?.removeEventListener('submit', this.boundSubmit)
  }

  get strictValidation () {
    return this.hasMessagesValue && this.hasErrorTarget
  }

  loadTel(){
    const iti = this.iti = window.intlTelInput(this.inputTarget, {
      initialCountry: this.codeValue,
      formatOnDisplay: true,
      separateDialCode: true,
      autoPlaceholder: 'polite',
      geoIpLookup: callback => {
        fetch('https://ipapi.co/json')
          .then(res => res.json())
          .then(data => callback(data.country_code))
      },
      utilsScript:
        'https://cdn.jsdelivr.net/npm/intl-tel-input@19.5.6/build/js/utils.js'
    })
    this.countryCodeTarget.value = iti.getSelectedCountryData().iso2
    this.inputTarget.addEventListener('countrychange', () => {
      const previousCountry = this.countryCodeTarget.value
      this.countryCodeTarget.value = null
      this.countryCodeTarget.value = iti.getSelectedCountryData().iso2

      // Vérifier si ce champ est une source (utilise maintenant phone_identifier)
      if (this.isSourceField()) {
        this.syncDestinationPhoneInputs(this.countryCodeTarget.value)
      }

      // The same digits mean another number under another flag: re-judge now.
      if (this.strictValidation && this.inputTarget.value.trim()) this.validate()
    });
  }

  observeValidation () {
    this.onBlur ||= () => this.validate()
    this.onInput ||= () => {
      this.errorTarget.hidden ? this.refreshValidity() : this.validate()
    }
    this.boundSubmit ||= this.onSubmit.bind(this)

    this.inputTarget.addEventListener('blur', this.onBlur)
    // Don't flag a number while it is being typed, only clear or update an
    // error the customer has already been shown. The native validity is kept
    // current on every keystroke regardless: progressive-save checks it on
    // blur, possibly before this controller's own blur handler runs.
    this.inputTarget.addEventListener('input', this.onInput)
    // A pre-filled number can only be judged once utils.js has loaded (if it
    // never does, the server-side check is the fallback).
    this.iti.promise?.then(() => this.refreshValidity(), () => {})

    this.form = this.inputTarget.form
    this.form?.addEventListener('submit', this.boundSubmit)
  }

  onSubmit (event) {
    if (this.validate()) return

    // Turbo skips a submission whose default was prevented. Several phone
    // fields may block the same submit: only the first one takes the focus.
    if (!event.defaultPrevented) this.inputTarget.focus()
    event.preventDefault()
  }

  // Returns whether the field may be submitted, and shows / clears its error.
  validate () {
    const message = this.refreshValidity()

    this.errorTarget.textContent = message
    this.errorTarget.hidden = !message
    this.errorTarget.id ||= `${this.inputTarget.id || this.inputTarget.name}-country-error`

    if (message) {
      this.inputTarget.setAttribute('aria-invalid', 'true')
      this.inputTarget.setAttribute('aria-describedby', this.errorTarget.id)
    } else {
      this.inputTarget.removeAttribute('aria-invalid')
      this.inputTarget.removeAttribute('aria-describedby')
    }

    return !message
  }

  // Sets the native validity (checkValidity, :invalid) without showing the
  // inline error, and returns the error message, if any.
  refreshValidity () {
    const error = this.isJudgeable() ? phoneValidationError(this.iti, this.inputTarget.value) : null
    const message = (error && this.messagesValue[error]) || ''

    this.inputTarget.setCustomValidity(message)
    return message
  }

  // A disabled or hidden field (e.g. a collapsed billing section) can't be
  // corrected by the customer, so it must not block the form.
  isJudgeable () {
    return !this.inputTarget.disabled && this.inputTarget.offsetParent !== null
  }

  // Détermine si ce champ est un champ source en utilisant phone_identifier ou chain-index 0
  isSourceField() {
    // Vérifier si c'est un champ avec chain-index 0 (premier dans la chaîne)
    const chainIndex = this.inputTarget.dataset.chainIndex
    if (chainIndex === '0') {
      return true
    }

    // Ou vérifier l'identifier value si elle existe
    return this.hasIdentifierValue && this.identifierValue === 'source'
  }

  // Détermine si un contrôleur est associé à un champ de destination
  isDestinationController(controller) {
    if (!controller.inputTarget) return false;

    // Vérifier si c'est un champ avec chain-index > 0
    const chainIndex = controller.inputTarget.dataset.chainIndex
    if (chainIndex && chainIndex !== '0') {
      return true
    }

    // Ou vérifier l'identifier value
    return controller.hasIdentifierValue && controller.identifierValue === 'destination'
  }

  syncDestinationPhoneInputs(countryCode) {
    // Trouver tous les contrôleurs phone-input
    const phoneInputControllers = this.application.controllers.filter(
      controller => controller.context.identifier === 'phone-input' && controller !== this
    );

    // Mettre à jour uniquement les contrôleurs qui sont des destinations
    phoneInputControllers.forEach(controller => {
      if (this.isDestinationController(controller) && controller.iti &&
          this.shouldSyncDestinationController(controller)) {
        controller.iti.setCountry(countryCode);
      }
    });
  }

  shouldSyncDestinationController(controller) {
    // Ops/back-office forms keep the previous permissive sync behaviour.
    if (!this.strictValidation) return true

    // In the widget, a destination holding its own, different number keeps its
    // own flag so it is not reinterpreted under the source field's country.
    return [ '', this.inputTarget.value ].includes(controller.inputTarget.value)
  }
}
